// src/pages/Settings.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Button, Card, List, Typography, Modal, Input, Popconfirm,
  message, Spin, Tooltip, Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, ClearOutlined,
} from '@ant-design/icons';
import ProductHeader from '../components/ProductHeader';
import VisitorTag from '../components/VisitorTag';
import { dbAPI, STORE_NAMES } from '../utils/db';
import { fetchWithAuth } from '../services/auth';
import {
  getModels, createModel, updateModel, deleteModel,
  STATIC_BASE, type BrandModel,
} from '../services/modelsAPI';
import {
  getCategories, updateCategory,
  STATIC_BASE as CAT_STATIC_BASE, type CategoryItem,
  getBrands, updateBrandImage, type BrandItem,
  getFounders, updateFounderImage, type FounderItem,
} from '../services/categoriesAPI';
import { API_BASE } from '../config';
import './Settings.css';

const { Text } = Typography;

// ─── Image resize util (Canvas, client-side) ──────────────────────────────────

const LOGO_W = 400;
const LOGO_H = 200;
const CAT_W  = 400;
const CAT_H  = 300;

function resizeImage(file: File, w = LOGO_W, h = LOGO_H): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      // contain-scaling: вписываем целиком без обрезки
      const scale = Math.min(w / img.width, h / img.height);
      const dw    = img.width  * scale;
      const dh    = img.height * scale;
      const dx    = (w - dw) / 2;
      const dy    = (h - dh) / 2;
      ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, dw, dh);
      canvas.toBlob((blob) => {
        const outName = file.name.replace(/\.[^.]+$/, '.png');
        resolve(new File([blob!], outName, { type: 'image/png' }));
      }, 'image/png', 0.92);
    };
    img.src = url;
  });
}

// ─── Categories section ────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  ignition_coils:           'Катушки зажигания',
  spark_plugs:              'Свечи зажигания',
  airbag_cables:            'Рулевые шлейфы',
  crankshaft_sensors:       'Датчики (Коленвал - Распредвал)',
  camshaft_sensors:         'Датчики (Разные)',
  ignition_coil_mouthpieces:'Провода/наконечники',
};

const CategoriesSection: React.FC = () => {
  const [cats, setCats]           = useState<CategoryItem[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<CategoryItem | null>(null);
  const [file, setFile]           = useState<File | null>(null);
  const [preview, setPreview]     = useState<string | null>(null);
  const fileInputRef              = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try { setCats(await getCategories()); }
    catch { message.error('Не удалось загрузить категории'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (c: CategoryItem) => {
    setEditing(c);
    setFile(null);
    setPreview(c.img ? `${CAT_STATIC_BASE}/${c.img}` : null);
    setModalOpen(true);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const resized = await resizeImage(f, CAT_W, CAT_H);
    setFile(resized);
    setPreview(URL.createObjectURL(resized));
  };

  const handleSave = async () => {
    if (!file) { message.warning('Выберите изображение'); return; }
    setSaving(true);
    try {
      await updateCategory(editing!.category_id, file);
      message.success('Изображение категории обновлено');
      setModalOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.message ?? 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card
        title="Изображения категорий"
        style={{ width: '100%' }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Фотографии, которые отображаются на сайте в блоке выбора категории товаров.
        </Text>

        {loading ? (
          <Spin />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {cats.map((c) => (
              <div
                key={c.category_id}
                style={{
                  textAlign: 'center', border: '1px solid #f0f0f0',
                  borderRadius: 8, padding: '10px 8px', position: 'relative',
                }}
              >
                <img
                  src={`${CAT_STATIC_BASE}/${c.img}`}
                  alt={c.name}
                  style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 4 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.15'; }}
                />
                <div style={{ fontSize: 11, marginTop: 4, fontWeight: 500 }}>
                  {CATEGORY_LABELS[c.name_] ?? c.name}
                </div>
                <div style={{ marginTop: 6 }}>
                  <Tooltip title="Заменить изображение">
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      onClick={() => openEdit(c)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        title={`Изображение: ${editing ? (CATEGORY_LABELS[editing.name_] ?? editing.name) : ''}`}
        okText="Сохранить"
        cancelText="Отмена"
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Text strong>Изображение</Text>
            {preview && (
              <div style={{ margin: '8px 0' }}>
                <img
                  src={preview}
                  alt="preview"
                  style={{ width: 120, height: 90, objectFit: 'cover', border: '1px solid #f0f0f0', borderRadius: 6 }}
                />
              </div>
            )}
            <br />
            <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
              Выбрать изображение
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
            {file && <Text type="secondary" style={{ marginLeft: 8 }}>{file.name}</Text>}
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Brands section ───────────────────────────────────────────────────────────

const BrandsSection: React.FC = () => {
  const [brands, setBrands]       = useState<BrandItem[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<BrandItem | null>(null);
  const [file, setFile]           = useState<File | null>(null);
  const [preview, setPreview]     = useState<string | null>(null);
  const fileInputRef              = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try { setBrands(await getBrands()); }
    catch { message.error('Не удалось загрузить бренды'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (b: BrandItem) => {
    setEditing(b);
    setFile(null);
    setPreview(b.image ? `${CAT_STATIC_BASE}/${b.image}` : null);
    setModalOpen(true);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const resized = await resizeImage(f, LOGO_W, LOGO_H);
    setFile(resized);
    setPreview(URL.createObjectURL(resized));
  };

  const handleSave = async () => {
    if (!file) { message.warning('Выберите изображение'); return; }
    setSaving(true);
    try {
      await updateBrandImage(editing!.id, file);
      message.success('Логотип бренда обновлён');
      setModalOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.message ?? 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card
        title="Логотипы производителей"
        style={{ width: '100%' }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Логотипы Bosch, NGK, Champion и др., отображаемые на сайте.
        </Text>

        {loading ? (
          <Spin />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {brands.map((b) => (
              <div
                key={b.id}
                style={{
                  textAlign: 'center', border: '1px solid #f0f0f0',
                  borderRadius: 8, padding: '10px 8px',
                }}
              >
                <img
                  src={b.image ? `${CAT_STATIC_BASE}/${b.image}` : ''}
                  alt={b.name}
                  style={{ width: 70, height: 50, objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.1'; }}
                />
                <div style={{ fontSize: 11, marginTop: 4, fontWeight: 500 }}>{b.name}</div>
                <div style={{ marginTop: 6 }}>
                  <Tooltip title="Заменить логотип">
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      onClick={() => openEdit(b)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        title={`Логотип: ${editing?.name ?? ''}`}
        okText="Сохранить"
        cancelText="Отмена"
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Text strong>Логотип</Text>
            {preview && (
              <div style={{ margin: '8px 0' }}>
                <img
                  src={preview}
                  alt="preview"
                  style={{ width: 100, height: 70, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 6 }}
                />
              </div>
            )}
            <br />
            <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
              Выбрать изображение
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
            {file && <Text type="secondary" style={{ marginLeft: 8 }}>{file.name}</Text>}
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Founders section (О Нас — фото основателя/соучредителя) ────────────────────

const FOUNDER_ROLE_LABEL: Record<string, string> = {
  founder:   'Основатель',
  cofounder: 'Соучредитель',
};

const FoundersSection: React.FC = () => {
  const [founders, setFounders]   = useState<FounderItem[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<FounderItem | null>(null);
  const [file, setFile]           = useState<File | null>(null);
  const [preview, setPreview]     = useState<string | null>(null);
  const fileInputRef              = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try { setFounders(await getFounders()); }
    catch { message.error('Не удалось загрузить основателей'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (f: FounderItem) => {
    setEditing(f);
    setFile(null);
    setPreview(f.image ? `${CAT_STATIC_BASE}/${f.image}` : null);
    setModalOpen(true);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const resized = await resizeImage(f, 500, 600); // портрет
    setFile(resized);
    setPreview(URL.createObjectURL(resized));
  };

  const handleSave = async () => {
    if (!file) { message.warning('Выберите изображение'); return; }
    setSaving(true);
    try {
      await updateFounderImage(editing!.id, file);
      message.success('Фото обновлено');
      setModalOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.message ?? 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  const label = (f: FounderItem) => FOUNDER_ROLE_LABEL[f.role] ?? f.role;

  return (
    <>
      <Card title="Основатели (страница «О Нас»)" style={{ width: '100%' }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Фотографии основателя и соучредителя на странице «О Нас». Имена и роли
          задаются в переводах сайта.
        </Text>

        {loading ? (
          <Spin />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {founders.map((f) => (
              <div
                key={f.id}
                style={{
                  textAlign: 'center', border: '1px solid #f0f0f0',
                  borderRadius: 8, padding: '10px 8px',
                }}
              >
                <img
                  src={f.image ? `${CAT_STATIC_BASE}/${f.image}` : ''}
                  alt={label(f)}
                  style={{ width: 100, height: 120, objectFit: 'cover', borderRadius: 6 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.1'; }}
                />
                <div style={{ fontSize: 12, marginTop: 6, fontWeight: 500 }}>{label(f)}</div>
                <div style={{ marginTop: 6 }}>
                  <Tooltip title="Заменить фото">
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      onClick={() => openEdit(f)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        title={`Фото: ${editing ? label(editing) : ''}`}
        okText="Сохранить"
        cancelText="Отмена"
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Text strong>Фотография</Text>
            {preview && (
              <div style={{ margin: '8px 0' }}>
                <img
                  src={preview}
                  alt="preview"
                  style={{ width: 120, height: 144, objectFit: 'cover', border: '1px solid #f0f0f0', borderRadius: 6 }}
                />
              </div>
            )}
            <br />
            <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
              Выбрать изображение
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
            {file && <Text type="secondary" style={{ marginLeft: 8 }}>{file.name}</Text>}
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Models section ────────────────────────────────────────────────────────────

type ModalMode = 'add' | 'edit';

const ModelsSection: React.FC = () => {
  const [carModels, setCarModels] = useState<BrandModel[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode]           = useState<ModalMode>('add');
  const [editing, setEditing]     = useState<BrandModel | null>(null);
  const [name, setName]           = useState('');
  const [file, setFile]           = useState<File | null>(null);
  const [preview, setPreview]     = useState<string | null>(null);
  const fileInputRef              = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try { setCarModels(await getModels()); }
    catch { message.error('Не удалось загрузить бренды автомобилей'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setMode('add');
    setEditing(null);
    setName('');
    setFile(null);
    setPreview(null);
    setModalOpen(true);
  };

  const openEdit = (b: BrandModel) => {
    setMode('edit');
    setEditing(b);
    setName(b.name);
    setFile(null);
    setPreview(`${STATIC_BASE}/${b.img}`);
    setModalOpen(true);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const resized = await resizeImage(f);
    setFile(resized);
    setPreview(URL.createObjectURL(resized));
  };

  const handleSave = async () => {
    if (!name.trim()) { message.warning('Введите название бренда'); return; }
    if (mode === 'add' && !file) { message.warning('Выберите изображение'); return; }
    setSaving(true);
    try {
      if (mode === 'add') {
        await createModel(name.trim(), file!);
        message.success('Бренд добавлен');
      } else {
        await updateModel(editing!.id, name.trim(), editing!.img, file ?? undefined);
        message.success('Бренд обновлён');
      }
      setModalOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.message ?? 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b: BrandModel) => {
    try {
      await deleteModel(b.id);
      message.success('Бренд удалён');
      await load();
    } catch (e: any) {
      message.error(e?.message ?? 'Ошибка при удалении');
    }
  };

  return (
    <>
      <Card
        title="Бренды / Модели автомобилей"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Добавить
          </Button>
        }
        style={{ width: '100%' }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Логотипы брендов отображаются на сайте как фильтр товаров по OEM-совместимости.
        </Text>

        {loading ? (
          <Spin />
        ) : (
          <div className="settings-models-scroll" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: 292, overflowY: 'auto' }}>
            {carModels.map((b) => (
              <div
                key={b.id}
                style={{
                  width: 110, textAlign: 'center', border: '1px solid #f0f0f0',
                  borderRadius: 8, padding: '10px 8px', position: 'relative',
                }}
              >
                <img
                  src={`${STATIC_BASE}/${b.img}`}
                  alt={b.name}
                  style={{ width: 70, height: 70, objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <div style={{ fontSize: 11, marginTop: 4, fontWeight: 500 }}>{b.name}</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 6 }}>
                  <Tooltip title="Редактировать">
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openEdit(b)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title={`Удалить бренд «${b.name}»?`}
                    onConfirm={() => handleDelete(b)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Tooltip title="Удалить">
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        title={mode === 'add' ? 'Добавить бренд' : 'Редактировать бренд'}
        okText="Сохранить"
        cancelText="Отмена"
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Text strong>Название</Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: BMW"
              style={{ marginTop: 4 }}
            />
          </div>
          <div>
            <Text strong>Логотип</Text>
            {preview && (
              <div style={{ margin: '8px 0' }}>
                <img
                  src={preview}
                  alt="preview"
                  style={{ width: 80, height: 80, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 6 }}
                />
              </div>
            )}
            <br />
            <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
              {mode === 'edit' ? 'Заменить изображение' : 'Выбрать изображение'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
            {file && <Text type="secondary" style={{ marginLeft: 8 }}>{file.name}</Text>}
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Main Settings page ────────────────────────────────────────────────────────

const CACHE_STORES = [
  { key: STORE_NAMES.notifications, label: 'Уведомления о заказах' },
  { key: STORE_NAMES.questions,     label: 'Сообщения покупателей' },
  { key: STORE_NAMES.inbounds,      label: 'Поступления (кэш)'     },
];

const OrdersResetSection: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (confirmText !== 'УДАЛИТЬ') return;
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/order/all`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`${res.status}`);
      // Очищаем локальный кэш уведомлений
      await dbAPI.clear(STORE_NAMES.notifications);
      message.success('Все заказы удалены из MySQL и кэша');
      setModalOpen(false);
      setConfirmText('');
    } catch {
      message.error('Ошибка при удалении заказов');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Аварийный сброс заказов (MySQL)" style={{ width: '100%' }}>
      <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
        ⚠️ Удаляет ВСЕ заказы из MySQL: <b>ordered_products</b>, <b>order_discounts</b>, <b>onorder</b>.
        Действие необратимо.
      </Text>
      <Button danger icon={<DeleteOutlined />} onClick={() => setModalOpen(true)}>
        Сбросить все заказы
      </Button>

      <Modal
        title="⚠️ Подтверждение удаления заказов"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setConfirmText(''); }}
        footer={[
          <Button key="cancel" onClick={() => { setModalOpen(false); setConfirmText(''); }}>
            Отмена
          </Button>,
          <Button
            key="confirm" danger
            disabled={confirmText !== 'УДАЛИТЬ'}
            loading={loading}
            onClick={handleReset}
          >
            Удалить все заказы
          </Button>,
        ]}
      >
        <Text type="danger">
          Это действие удалит ВСЕ записи заказов из базы данных MySQL без возможности восстановления.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Text>Введите <b>УДАЛИТЬ</b> для подтверждения:</Text>
          <Input
            style={{ marginTop: 8 }}
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="УДАЛИТЬ"
          />
        </div>
      </Modal>
    </Card>
  );
};

const CacheSection: React.FC = () => {
  const [clearing, setClearing] = useState(false);

  const handleClear = async () => {
    setClearing(true);
    try {
      for (const s of CACHE_STORES) await dbAPI.clear(s.key);
      message.success('Кэш очищен. Данные обновятся при следующем открытии страницы.');
    } catch {
      message.error('Ошибка при очистке кэша');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card title="Локальный кэш (IndexedDB)" style={{ width: '100%' }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Очищает локально кэшированные данные уведомлений, сообщений и поступлений.
        MySQL не затрагивается — данные восстановятся при следующей загрузке страницы.
      </Text>
      <List
        size="small"
        dataSource={CACHE_STORES}
        renderItem={s => <List.Item>{s.label}</List.Item>}
        style={{ marginBottom: 16 }}
      />
      <Popconfirm
        title="Очистить локальный кэш?"
        description="MySQL не пострадает. Данные загрузятся заново при открытии страниц."
        okText="Очистить" cancelText="Отмена"
        onConfirm={handleClear}
      >
        <Button danger icon={<ClearOutlined />} loading={clearing}>
          Очистить кэш
        </Button>
      </Popconfirm>
    </Card>
  );
};

const Settings: React.FC = () => {
  const tabItems = [
    { key: 'categories', label: 'Категории',                 children: <CategoriesSection /> },
    { key: 'carBrands',  label: 'Бренды авто',               children: <ModelsSection /> },
    { key: 'logos',      label: 'Логотипы производителей',   children: <BrandsSection /> },
    { key: 'founders',   label: 'Основатели',               children: <FoundersSection /> },
    { key: 'system',     label: 'Система · кэш',             children: <CacheSection /> },
    {
      key: 'danger',
      label: <span style={{ color: '#cf1322' }}>⚠️ Опасная зона</span>,
      children: <OrdersResetSection />,
    },
  ];

  return (
    <div className="settings-page" style={{ padding: 20 }}>
      <ProductHeader title={<h2 style={{ margin: 0 }}>Настройки</h2>} addButton={<VisitorTag />} />

      <Tabs
        tabPosition="left"
        items={tabItems}
        defaultActiveKey="categories"
        style={{ marginTop: 8, minHeight: 480 }}
      />
    </div>
  );
};

export default Settings;
